// /workspace for someone with no videos of their own yet: what they'll use Bumpups for (one click,
// optional), then the quickest ways in — their own upload, a YouTube link, or a ready-made example
// that opens instantly without using minutes.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faCircleNotch, faXmark, faPlay } from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { USE_CASES, findUseCase } from './useCases';
import { EXAMPLES, exampleThumb } from './examples';
import { track } from '../../analytics/track';
import './getStarted.css';

const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export default function GetStarted({ useCase, onPick, onDismiss, onUpload, onPasteLink, uploadNote }) {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(null);
  const [failed, setFailed] = useState(false);
  const picked = findUseCase(useCase);
  const shown = picked || findUseCase('other');

  const openExample = async id => {
    if (opening) return;
    setOpening(id);
    setFailed(false);
    track('example_opened', { example: id, useCase: useCase || 'none' });
    try {
      const { data } = await httpsCallable(getFunctions(), 'openExampleWorkspace')({ exampleId: id });
      navigate(`/workspace/${data.content_id.replace(/^workspace-/, '')}`);
    } catch {
      setFailed(true);
      setOpening(null);
    }
  };

  return (
    <section className="gs" aria-labelledby="gs-title">
      <div className="gs-head">
        <div>
          <h2 className="gs-title" id="gs-title">{picked ? 'Here’s the fastest way in' : 'What will you use Bumpups for?'}</h2>
          <p className="gs-sub">
            {picked ? 'Pick any of these. You can change your answer anytime.' : 'One click, and we’ll tailor what you see. Optional.'}
          </p>
        </div>
        <button type="button" className="gs-close" onClick={onDismiss} aria-label="Hide getting started" title="Hide">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className="gs-chips" role="radiogroup" aria-label="What will you use Bumpups for?">
        {USE_CASES.map(u => (
          <button
            key={u.id}
            type="button"
            role="radio"
            aria-checked={useCase === u.id}
            aria-label={u.label}
            className={`gs-chip${useCase === u.id ? ' is-active' : ''}`}
            onClick={() => onPick(u.id)}
          >
            <FontAwesomeIcon icon={u.icon} className="gs-chip-icon" />
            <span className="gs-chip-long">{u.label}</span>
            <span className="gs-chip-short">{u.short}</span>
          </button>
        ))}
      </div>

      <div className="gs-actions">
        <button type="button" className="gs-action gs-action--primary" onClick={onUpload}>
          <span className="gs-action-icon"><FontAwesomeIcon icon={faArrowUpFromBracket} /></span>
          <span className="gs-action-text">
            <span className="gs-action-title">{shown.upload}</span>
            <span className="gs-action-note">{uploadNote}</span>
          </span>
        </button>
        <button type="button" className="gs-action" onClick={onPasteLink}>
          <span className="gs-action-icon gs-action-icon--yt"><FontAwesomeIcon icon={faYoutube} /></span>
          <span className="gs-action-text">
            <span className="gs-action-title">Paste a YouTube link</span>
            <span className="gs-action-note">Uses your free minutes. Ready in about a minute.</span>
          </span>
        </button>
      </div>

      <p className="gs-label">Or open an example. It’s ready instantly and uses no minutes.</p>
      <div className="gs-examples">
        {shown.examples.map(id => {
          const ex = EXAMPLES[id];
          return (
            <button key={id} type="button" className="gs-example" onClick={() => openExample(id)} disabled={!!opening}>
              <span className="gs-example-thumb">
                <img src={exampleThumb(ex.videoId)} alt="" loading="lazy" width="320" height="180" />
                <span className="gs-example-duration">{fmt(ex.seconds)}</span>
                <span className="gs-example-play">
                  <FontAwesomeIcon icon={opening === id ? faCircleNotch : faPlay} spin={opening === id} />
                </span>
              </span>
              <span className="gs-example-title">{ex.title}</span>
              <span className="gs-example-by">{ex.by}</span>
            </button>
          );
        })}
      </div>
      {failed && <p className="gs-error" role="alert">That example didn’t open. Please try again.</p>}
    </section>
  );
}
